<script lang="ts">
  import type { BuildState } from '../build-state.svelte.js';
  import { applyUrlToBuild } from '../build-url.js';
  import { lang as langState } from '../lang-state.svelte.js';

  interface Props {
    build: BuildState;
    /** Called after preset applied so parent can switch to Build tab */
    onAfterApply?: (id: string) => void;
  }

  let { build, onAfterApply }: Props = $props();
  let lang = $derived(langState.current);

  interface Preset {
    id: string;
    name_en: string;
    name_ru: string;
    desc_en: string;
    desc_ru: string;
    color?: string;
    params: string;
  }

  let presets = $state<Preset[]>([]);
  let loadError = $state<string | null>(null);
  let loadedId = $state<string | null>(null);

  $effect(() => {
    fetch('/data/preset-builds.json', { cache: 'no-cache' })
      .then((r) => r.json())
      .then((d) => { presets = d.presets ?? []; })
      .catch((e) => { loadError = String(e); });
  });

  function apply(p: Preset) {
    build.reset();
    const params = new URLSearchParams(p.params);
    applyUrlToBuild(build, params);
    loadedId = p.id;
    try {
      const newQs = '?' + p.params;
      history.replaceState(null, '', newQs + '#build');
    } catch { /* ignore */ }
    onAfterApply?.(p.id);
  }
</script>

<section class="panel bp">
  <div class="panel-title">
    <span>🎁 {lang === 'ru' ? 'Готовые тестовые билды' : 'Sample builds'}</span>
  </div>
  {#if loadError}
    <div class="bp-error">{loadError}</div>
  {/if}
  <div class="bp-grid">
    {#each presets as p (p.id)}
      <button
        class="bp-btn"
        data-color={p.color ?? 'orange'}
        class:active={loadedId === p.id}
        onclick={() => apply(p)}
      >
        <div class="bp-name">{lang === 'ru' ? p.name_ru : p.name_en}</div>
        <div class="bp-desc">{lang === 'ru' ? p.desc_ru : p.desc_en}</div>
      </button>
    {/each}
  </div>
  <div class="bp-note">
    {lang === 'ru'
      ? 'Клик — заполняет калькулятор полностью (оружие + сет + бренды + spec + HS + условия). Можно пере-собрать своими руками.'
      : 'Click — populates calculator fully (weapon + set + brands + spec + HS + conditions). Free to tweak after.'}
  </div>
</section>

<style>
  .bp { max-width: 1100px; margin: 0 auto 12px; }
  .bp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 8px; }
  .bp-btn {
    display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
    padding: 10px 12px;
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-left: 3px solid var(--orange);
    border-radius: var(--r-sm);
    cursor: pointer;
    text-align: left;
    transition: background .12s, border-color .12s, box-shadow .12s;
  }
  .bp-btn[data-color="red"]    { border-left-color: var(--exotic); }
  .bp-btn[data-color="orange"] { border-left-color: var(--named); }
  .bp-btn[data-color="blue"]   { border-left-color: var(--brand); }
  .bp-btn:hover { background: var(--card-2); border-color: var(--border-hi); }
  .bp-btn.active { background: rgba(254,175,16,.08); border-color: var(--orange); box-shadow: var(--glow-amber); }
  .bp-name { font: 700 11px/1 var(--f-display); letter-spacing: .08em; color: var(--text); text-transform: uppercase; }
  .bp-desc { font-size: 10.5px; color: var(--text-dim); line-height: 1.45; }
  .bp-note { margin-top: 10px; font-size: 10px; color: var(--muted); font-style: italic; text-align: center; }
  .bp-error { padding: 8px; color: var(--red); font-size: 11px; }
</style>
